import { t } from "../i18n";
import { CAST, castName, pimg } from "../art/cast";
import { $, h } from "../lib/dom";
import { renderSide } from "./dialog";
import { resetHelp, showHelpOnce } from "./help";
import { hideThinking } from "./llm";
import { post } from "./log";
import { placePlayer } from "./player";
import { fitCanvas } from "./camera";
import { resetDrawer, snapDrawer } from "./drawer";
import { beginSession, endSession, L, S } from "./state";
import { renderHUD, spend } from "./time";
import { bestGrade, lastDept, rememberDept } from "./progress";
import type { Department, Level } from "./types";
import { buildLevelMap } from "./world";
import { closeZoom } from "./zoom";

let departments: readonly Department[] = [];
/** Department shown on the title screen. */
let tab: Department | undefined;

export function registerDepartments(list: readonly Department[]): void {
  departments = list;
  tab = list.find((d) => d.id === lastDept()) ?? list[0];
}

const button = (text: string, className = "btn"): HTMLButtonElement => h("button", { className, text });

function levelCard(d: Department, lv: Level): HTMLButtonElement {
  const best = bestGrade(lv.id);
  const b = h(
    "button",
    { className: "btn lvcard" },
    h("div", { className: "no", text: lv.no }),
    h("div", {}, h("b", { text: lv.title }), h("span", { text: `${lv.place}${t("help.legendSep")}${lv.blurb}` })),
  );
  if (best) {
    const g = h("div", { className: "best", text: best });
    g.title = t("menu.best");
    b.appendChild(g);
  }
  b.onclick = () => levelIntro(d, lv);
  return b;
}

export function titleScreen(): void {
  const w = $("#titleWin");
  const d = tab;
  const tabs = h(
    "div",
    { className: "depts" },
    ...departments.map((x) => {
      const b = button(x.name, x === d ? "btn tab on" : "btn tab");
      b.setAttribute("aria-pressed", String(x === d));
      b.onclick = () => {
        tab = x;
        rememberDept(x.id);
        titleScreen();
      };
      return b;
    }),
  );
  const body: HTMLElement[] = [];
  if (d) {
    body.push(h("p", { className: "sub", text: d.tagline }));
    if (d.levels.length) body.push(h("div", { className: "levels" }, ...d.levels.map((lv) => levelCard(d, lv))));
    else body.push(h("p", { className: "note", text: t("menu.empty") }));
    if (d.note) {
      const note = h("p", { className: "note", text: d.note });
      note.style.marginTop = "18px";
      body.push(note);
    }
  }
  // The game logo above the window (title/) names the game; the window holds just the menu.
  w.replaceChildren(tabs, ...body);
}

function levelIntro(d: Department, lv: Level): void {
  const w = $("#titleWin");
  const cast = h(
    "div",
    { className: "cast" },
    ...lv.cast.map((k) =>
      h(
        "figure",
        {},
        pimg(k),
        h("figcaption", { text: CAST[k]?.name ?? k }, h("small", { text: CAST[k]?.role ?? "" })),
      ),
    ),
  );
  const go = button(t("menu.start"), "btn primary");
  go.onclick = () => startLevel(d, lv);
  const back = button(t("menu.back"));
  back.onclick = titleScreen;
  w.replaceChildren(
    h("h1", { text: lv.title }),
    h("p", { className: "sub", text: t("menu.levelSub", { dept: d.name, level: lv.short }) }),
    cast,
    ...lv.intro.map((p) => h("p", { text: p })),
    h("p", {
      className: "note",
      text: t("menu.howto"),
    }),
    h("div", { className: "row" }, go, back),
  );
  go.focus();
}

/** Labels in the static markup that name the attending. */
function labelAttending(d: Department): void {
  const name = castName(d.attending);
  for (const id of ["#finishBtn", "#endFb"]) $(id).textContent = t("stage.feedback", { name });
  $("#call h3").textContent = t("call.title", { name });
}

function startLevel(d: Department, lv: Level): void {
  beginSession(d, lv);
  labelAttending(d);
  placePlayer(lv.player);
  buildLevelMap();
  $("#title").style.display = "none";
  $("#app").hidden = false;
  S.started = true;
  $("#stage").classList.remove("cine");
  fitCanvas();
  snapDrawer();
  renderSide();
  renderHUD();
  post("sys", "narr", null, lv.intro.at(-1) ?? "", { silent: true });
  showHelpOnce(lv.id, () => {
    if (S.live && L === lv) {
      lv.onStart?.();
      spend(1);
    }
  });
}

const OVERLAYS = ["#call", "#debrief", "#endCard", "#help", "#banner", "#pip", "#cineBox", "#evBanner"];

export function backToTitle(): void {
  closeZoom();
  endSession();
  placePlayer([0, 0]);
  hideThinking();
  resetHelp();
  for (const id of OVERLAYS) $(id).classList.remove("show");
  for (const id of ["#toasts", "#log", "#choices", "#sideHead", "#hudWaits"]) $(id).replaceChildren();
  $("#composer").hidden = true;
  $<HTMLTextAreaElement>("#input").value = "";
  $("#finishBtn").hidden = true;
  $("#hint").style.display = "none";
  $("#stage").classList.remove("cine", "shake");
  resetDrawer();
  $("#app").hidden = true;
  $("#title").style.display = "";
  titleScreen();
}
